/**
 * PATH: apps/quote/src/components/builder/application-selector-modal/ApplicationSelectorModal.tsx
 * PURPOSE: Full-screen modal orchestrator for browsing/selecting applications with AI ranking
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Search, X, Package, Loader2, Sparkles, Filter } from 'lucide-react'
import { cn } from '@/lib/utils'
import { quoteApi, type Application, type Supplier, type ProductCategory } from '@/lib/quoteApi'
import { type ApplicationSelectorModalProps, type FilterState, INITIAL_FILTERS } from './types'
import FilterPanel from './FilterPanel'
import ApplicationResultItem from './ApplicationResultItem'
import ApplicationPreviewPanel from './ApplicationPreviewPanel'

export default function ApplicationSelectorModal({
  isOpen, onClose, onSelect, context, selectedId,
}: ApplicationSelectorModalProps) {
  const [search, setSearch] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS)
  const [previewApp, setPreviewApp] = useState<Application | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Data fetching
  const { data: applications = [], isLoading } = useQuery({
    queryKey: ['applications'], queryFn: () => quoteApi.listApplications(),
    staleTime: 5 * 60 * 1000, enabled: isOpen,
  })
  const { data: suppliers = [] } = useQuery<Supplier[]>({
    queryKey: ['suppliers'], queryFn: () => quoteApi.listSuppliers(),
    staleTime: 10 * 60 * 1000, enabled: isOpen,
  })
  const semanticSearch = useMutation({
    mutationFn: async (query: string) => { if (!query || query.length < 3) return null; return quoteApi.searchApplications(query) },
  })

  // Filtering & ranking
  const filteredAndRanked = useMemo(() => {
    let results = [...applications]
    if (filters.areaType !== 'all') results = results.filter(a => a.area_type === filters.areaType)
    if (filters.substrateType !== 'all') results = results.filter(a => a.substrate_type === filters.substrateType)
    if (filters.exposureType !== 'all') results = results.filter(a => a.exposure_type === filters.exposureType)
    if (filters.supplierId !== 'all') results = results.filter(a => a.supplier_id === filters.supplierId)
    if (filters.categoryId !== 'all') results = results.filter(a => a.category_id === filters.categoryId)
    if (search.trim()) {
      const q = search.toLowerCase()
      results = results.filter(app =>
        app.menu_item?.toLowerCase().includes(q) || app.name?.toLowerCase().includes(q) ||
        app.code?.toLowerCase().includes(q) || app.product_name?.toLowerCase().includes(q) ||
        app.supplier_name?.toLowerCase().includes(q) || app.description?.toLowerCase().includes(q) ||
        app.application_situation?.toLowerCase().includes(q)
      )
    }
    results.sort((a, b) => {
      if (a.id === selectedId) return -1
      if (b.id === selectedId) return 1
      const diff = (b.usage_count || 0) - (a.usage_count || 0)
      if (diff !== 0) return diff
      return (a.menu_item || a.name || '').localeCompare(b.menu_item || b.name || '')
    })
    return results.slice(0, 100)
  }, [applications, filters, search, selectedId])

  const displayResults = semanticSearch.data?.length ? semanticSearch.data : filteredAndRanked

  const activeFilterCount = useMemo(() => Object.values(filters).filter(v => v !== 'all').length, [filters])

  // Effects
  useEffect(() => {
    if (isOpen) { setTimeout(() => inputRef.current?.focus(), 100); setHighlightedIndex(0); setSearch(''); if (context?.description) setSearch(context.description.slice(0, 50)) }
  }, [isOpen, context])

  useEffect(() => {
    if (!search || search.length < 3) { semanticSearch.reset(); return }
    const timer = setTimeout(() => { semanticSearch.mutate(search) }, 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    if (listRef.current && displayResults.length > 0) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement
      if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [highlightedIndex, displayResults])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown': e.preventDefault(); setHighlightedIndex(i => Math.min(i + 1, displayResults.length - 1)); break
        case 'ArrowUp': e.preventDefault(); setHighlightedIndex(i => Math.max(i - 1, 0)); break
        case 'Enter': e.preventDefault(); if (displayResults[highlightedIndex]) handleSelect(displayResults[highlightedIndex]); break
        case 'Escape': e.preventDefault(); onClose(); break
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, displayResults, highlightedIndex, onClose])

  // Handlers
  const handleSelect = useCallback((app: Application) => { onSelect(app); onClose() }, [onSelect, onClose])
  const handleFilterChange = useCallback((key: keyof FilterState, value: string) => { setFilters(f => ({ ...f, [key]: value })); setHighlightedIndex(0) }, [])
  const clearFilters = useCallback(() => { setFilters(INITIAL_FILTERS) }, [])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-5xl h-[85vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-slate-200 dark:border-slate-700">
          <div className="p-4 flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input ref={inputRef} type="text" value={search} onChange={(e) => { setSearch(e.target.value); setHighlightedIndex(0) }} placeholder="Search applications by name, code, supplier, situation..." className="w-full pl-10 pr-4 py-3 text-base border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all" />
              {semanticSearch.isPending && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-indigo-500 animate-spin" />}
              {semanticSearch.data?.length ? <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400"><Sparkles className="w-3 h-3" />AI</div> : null}
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={cn('px-4 py-3 rounded-xl border flex items-center gap-2 transition-colors', showFilters || activeFilterCount > 0 ? 'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800')}>
              <Filter className="w-4 h-4" /> Filters
              {activeFilterCount > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-indigo-600 text-white rounded-full">{activeFilterCount}</span>}
            </button>
            <button onClick={onClose} className="p-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"><X className="w-5 h-5" /></button>
          </div>
          {showFilters && <FilterPanel filters={filters} suppliers={suppliers} activeFilterCount={activeFilterCount} onFilterChange={handleFilterChange} onClearFilters={clearFilters} />}
        </div>

        {/* Body */}
        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 overflow-y-auto" ref={listRef}>
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-indigo-500 mb-3" /><p className="text-slate-500">Loading applications...</p></div>
            ) : displayResults.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64"><Package className="w-12 h-12 text-slate-300 mb-3" /><p className="text-lg font-medium text-slate-700 dark:text-slate-300">No applications found</p><p className="text-sm text-slate-500 mt-1">Try adjusting your search or filters</p></div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {displayResults.map((app, index) => (
                  <ApplicationResultItem key={app.id} app={app} index={index} highlightedIndex={highlightedIndex} selectedId={selectedId} onSelect={handleSelect} onHover={(a, i) => { setHighlightedIndex(i); setPreviewApp(a) }} />
                ))}
              </div>
            )}
          </div>
          {previewApp && <ApplicationPreviewPanel app={previewApp} onSelect={handleSelect} />}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-700 rounded border text-[10px]">↑↓</kbd> Navigate</span>
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-700 rounded border text-[10px]">↵</kbd> Select</span>
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-700 rounded border text-[10px]">esc</kbd> Close</span>
          </div>
          <div className="text-xs text-slate-500">
            {displayResults.length} applications
            {semanticSearch.data?.length ? <span className="ml-2 text-indigo-600 dark:text-indigo-400">• AI-ranked</span> : null}
          </div>
        </div>
      </div>
    </div>
  )
}
